import type { HandleCloseParams, IOverlayItemModel } from './types';

// @analyze-exports:disable
export class OverlayManager {
    private static instance: OverlayManager;

    static getInstance() {
        if (!this.instance) {
            this.instance = new OverlayManager();
        }

        return this.instance;
    }

    private overlays: IOverlayItemModel[] = [];
    private eventTarget: EventTarget | null = null;
    private activeOverlay?: IOverlayItemModel | null;

    count() {
        return this.overlays.length;
    }

    addOverlay(model: IOverlayItemModel) {
        if (this.overlays.length === 0) {
            this.attachEvents();
        }

        const idx = this.overlays.findLastIndex((item) => item.order <= model.order) + 1;
        this.overlays.splice(idx, 0, model);
    }

    removeOverlay(model: IOverlayItemModel) {
        const index = this.overlays.indexOf(model);
        if (index < 0) {
            return;
        }

        this.overlays.splice(index, 1);

        if (this.overlays.length === 0) {
            this.detachEvents();
        }
    }

    getTopOverlayOptions(): IOverlayItemModel | undefined {
        return this.overlays[this.overlays.length - 1];
    }

    private tryCloseOverlay(model: IOverlayItemModel, params: HandleCloseParams): boolean {
        if (!model.canClose(params)) {
            return false;
        }

        model.close(params);

        return true;
    }

    private handleClose(params: HandleCloseParams): void {
        const activeOverlay = params.activeOverlay || this.activeOverlay;

        if (params.source === 'click' && params.target) {
            const topOverlay = activeOverlay || this.getTopOverlayOptions();

            if (topOverlay && topOverlay.containsTarget(params.target)) {
                const isCloseElement =
                    params.target instanceof HTMLElement &&
                    params.target.closest('[data-overlay-close-element="true"]');

                if (!isCloseElement) {
                    return;
                }
            }
        }

        if (params.activeOverlay && this.overlays.includes(params.activeOverlay)) {
            this.tryCloseOverlay(params.activeOverlay, params);

            if (params.source === 'esc') {
                return;
            }
        }

        for (let i = this.overlays.length - 1; i >= 0; --i) {
            const options = this.overlays[i];

            if (options && options !== params.activeOverlay) {
                this.tryCloseOverlay(options, params);

                if (params.source === 'esc') {
                    return;
                }
            }
        }

        if (params.source === 'click' && params.strategy === 'pressup') {
            setTimeout(() => {
                this.overlays.forEach((item) => {
                    item.cleanAfterCloses();
                });
            }, 1);
        }
    }

    private onDocumentKeyUp = (event: KeyboardEvent): void => {
        const key = event.key;

        if (key !== 'Escape' && key !== 'Esc') {
            return;
        }

        this.handleClose({ event, target: event.target, source: 'esc' });
    };

    private onDocumentMouseDown = (event: MouseEvent): void => {
        if (event.button > 0) {
            return;
        }

        const topOverlay = this.getTopOverlayOptions();

        this.eventTarget = event.target;
        this.activeOverlay = topOverlay;

        this.handleClose({
            event,
            target: event.target,
            source: 'click',
            strategy: 'pressdown',
            activeOverlay: topOverlay,
        });
    };

    private onDocumentClick = (event: MouseEvent): void => {
        if (event.button > 0) {
            return;
        }
        const target = this.eventTarget;
        this.eventTarget = null;

        const activeOverlay = this.activeOverlay;
        this.activeOverlay = null;

        if (!target || !activeOverlay) {
            return;
        }

        this.handleClose({ event, target, source: 'click', strategy: 'pressup', activeOverlay });
    };

    private attachEvents() {
        document.addEventListener('keyup', this.onDocumentKeyUp);
        document.addEventListener('mousedown', this.onDocumentMouseDown, true);
        document.addEventListener('click', this.onDocumentClick, true);
    }

    private detachEvents() {
        document.removeEventListener('keyup', this.onDocumentKeyUp);
        document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
        document.removeEventListener('click', this.onDocumentClick, true);
    }
}
